import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Box,
  InputAdornment,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Info from "@mui/icons-material/Info";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteVerificationToken,
  generateVerificationToken,
} from "../../../../features/auth/authSlice";
import colors from "../../../../Constants/colors";
import { textfieldLayout } from "./SettingsPageStyles";
import {
  deleteButtonStyles,
  iconButtonStyles,
  primaryButtonStyles,
} from "../../../../Constants/buttonStyles";
import { inputStyles } from "../../../../Constants/inputStyles";
import theme from "../../../../Constants/theme";

const VerificationToken = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { organization } = useSelector((state) => state.organization);

  // const [userToken, setUserToken] = useState(
  //   user?.user?.verification_token || ""
  // );

  const [tokenVisible, setTokenVisible] = useState(false);

  // useEffect(() => {
  //   if (user?.user?.verification_token) {
  //     setUserToken(user.user.verification_token);
  //   }
  // }, [user?.user?.verification_token]);

  const handleTokenVisibilityToggle = () => {
    setTokenVisible((prev) => !prev);
  };

  const handleCopyToken = () => {
    navigator.clipboard.writeText(user?.user?.verification_token);
    alert("Verification Code copied to clipboard."); // Replace with Snackbar if needed
  };

  const handleGenerateToken = async () => {
    await dispatch(generateVerificationToken({ email: user.user.email }));
  };

  const handleDeleteToken = async () => {
    await dispatch(deleteVerificationToken({ email: user.user.email }));
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "start",
        alignItems: "flex-start",
        width: "90%",
        padding: "40px",
        borderRadius: "10px",
        color: theme.textColors.dark.primary,
        backgroundColor: theme.main.backgroundBlue,
      }}
    >
      <Grid container spacing={2} mt={4}>
        {organization?.subscription === "starter" && (
          <Typography sx={{ textAlign: "center", width: "100%" }}>
            This feature is available in the Queryloop Pro and Enterprise
            packages.
          </Typography>
        )}

        {user?.user?.verification_token ? (
          <>
            <Grid item xs={10}>
              <Typography
                variant="caption"
                sx={{ display: "flex", alignItems: "center" }}
              >
                Verification Token &nbsp;
                <Tooltip
                  title={
                    <p>
                      Your verification token is used to securely authenticate
                      API requests by including it in the{" "}
                      <code>verification_token</code> field. This ensures that
                      only authorized interactions with the platform are
                      permitted. You can regenerate your token here if needed
                      for security.
                    </p>
                  }
                  arrow
                >
                  <Info sx={{ color: "white", fontSize: 18 }} />
                </Tooltip>
              </Typography>

              <TextField
                margin="dense"
                placeholder="Verification Token"
                type={tokenVisible ? "text" : "password"}
                fullWidth
                variant="outlined"
                value={user?.user?.verification_token}
                sx={{ mb: 2, ...textfieldLayout }}
                disabled
              />
            </Grid>
            <Grid item xs={2} sx={{ display: "flex", alignItems: "center" }}>
              <IconButton onClick={handleTokenVisibilityToggle}>
                {tokenVisible ? (
                  <VisibilityOffIcon sx={{ color: "white" }} />
                ) : (
                  <VisibilityIcon sx={{ color: "white" }} />
                )}
              </IconButton>
              {user?.user?.verification_token && (
                <Tooltip title="Copy to clipboard">
                  <IconButton onClick={handleCopyToken}>
                    <ContentCopyIcon sx={{ color: "white" }} />
                  </IconButton>
                </Tooltip>
              )}
            </Grid>
            <Grid item xs={12}>
              <Tooltip title="This will over-write current key.">
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "var(--buttonBackground)",
                    color: "white",
                    mt: 2,
                  }}
                  onClick={handleGenerateToken}
                >
                  Regenerate Token
                </Button>
              </Tooltip>
            </Grid>
            <Grid item xs={12}>
              <Tooltip title="Current key will be permanently deleted">
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "red",
                    color: "white",
                    mt: 2,
                  }}
                  onClick={handleDeleteToken}
                >
                  Delete Token
                </Button>
              </Tooltip>
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "var(--buttonBackground)",
                color: "white",
                mt: 2,
                ":disabled": {
                  color: "rgba(255, 255, 255, 0.25)",
                  cursor: "not-allowed",
                },
              }}
              disabled={organization?.subscription === "starter"}
              onClick={handleGenerateToken}
              endIcon={
                <Tooltip
                  title={
                    <>
                      <p>
                        Generate a verification token to securely authenticate
                        your API requests. This token is used in the{" "}
                        <code>verification_token</code> field of API calls,
                        allowing authorized access to the platform.
                      </p>
                      <p>
                        Click &nbsp;
                        <strong>'Generate Token'</strong> to create one.
                      </p>
                    </>
                  }
                  arrow
                >
                  <Info
                    sx={{
                      color:
                        organization?.subscription === "starter"
                          ? "rgba(255, 255, 255, 0.25)"
                          : "white",
                      fontSize: 18,
                    }}
                  />
                </Tooltip>
              }
            >
              Generate Token
            </Button>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default VerificationToken;
